import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, useRealtimeSubscription } from '@cleanpro/common-ui';
import { Business } from '../business';
import { BusinessContext, BusinessDataContextType } from './BusinessContextObject';

// Raw database row type
/**
 * Represents a business entity with detailed information including contact details,
 * address, business type, status, contract dates, services, schedule, ratings,
 * insurance, and metadata.
 *
 * @property {string} id - Unique identifier for the business.
 * @property {string} business_name - Name of the business.
 * @property {string} owner_name - Name of the business owner.
 * @property {string} email - Contact email address for the business.
 * @property {string} phone - Contact phone number for the business.
 * @property {string} [address_line_1] - Primary address line (optional).
 * @property {string} [address_line_2] - Secondary address line (optional).
 * @property {string} [city] - City where the business is located (optional).
 * @property {string} [state] - State where the business is located (optional).
 * @property {string} [postal_code] - Postal code for the business address (optional).
 * @property {string} business_type - Type/category of the business.
 * @property {string} status - Current status of the business (e.g., active, inactive).
 * @property {string} contract_start_date - Date when the business contract started.
 * @property {string[]} [services_offered] - List of services offered by the business (optional).
 * @property {number} hourly_rate - Hourly rate charged by the business.
 * @property {boolean} [monday] - Indicates if the business operates on Monday (optional).
 * @property {boolean} [tuesday] - Indicates if the business operates on Tuesday (optional).
 * @property {boolean} [wednesday] - Indicates if the business operates on Wednesday (optional).
 * @property {boolean} [thursday] - Indicates if the business operates on Thursday (optional).
 * @property {boolean} [friday] - Indicates if the business operates on Friday (optional).
 * @property {boolean} [saturday] - Indicates if the business operates on Saturday (optional).
 * @property {boolean} [sunday] - Indicates if the business operates on Sunday (optional).
 * @property {number} rating - Average rating of the business.
 * @property {number} completed_jobs - Number of jobs completed by the business.
 * @property {string} [insurance_provider] - Name of the insurance provider (optional).
 * @property {string} [insurance_policy_number] - Insurance policy number (optional).
 * @property {string} [insurance_expiration_date] - Expiration date of the insurance policy (optional).
 * @property {string} user_id - Identifier for the user associated with the business.
 * @property {string} created_at - Timestamp when the business record was created.
 * @property {string} updated_at - Timestamp when the business record was last updated.
 */
type BusinessRow = {
  id: string;
  business_name: string;
  owner_name: string;
  email: string | null;
  phone: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state?: string | null;
  postal_code?: string | null;
  business_type: string | null;
  status: string | null;
  landing_page_status: string | null;
  contract_start_date: string | null;
  services_offered?: string[] | null;
  service_postal_codes?: string[] | null;
  hourly_rate: number | null;
  monday?: boolean | null;
  tuesday?: boolean | null;
  wednesday?: boolean | null;
  thursday?: boolean | null;
  friday?: boolean | null;
  saturday?: boolean | null;
  sunday?: boolean | null;
  rating: number | null;
  completed_jobs: number | null;
  insurance_provider?: string | null;
  insurance_policy_number?: string | null;
  insurance_expiration_date?: string | null;
  user_id?: string | null;
  clerk_organization_id?: string | null;
  subscription_plan?: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/**
 * Provides business data and CRUD operations to its children via React Context.
 *
 * This context provider manages the state and operations for the "businesses" resource,
 * including fetching, creating, updating, and deleting businesses from the Supabase backend.
 * It also handles loading and error states, and supports fetching a single business by ID.
 *
 * @component
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The child components that will have access to the business context.
 *
 * @example
 * <BusinessDataProvider>
 *   <YourComponent />
 * </BusinessDataProvider>
 *
 * @context
 * Provides the following context value:
 * - businesses: Business[] - The list of businesses.
 * - loading: boolean - Loading state for business operations.
 * - error: string | null - Error message, if any.
 * - getBusinessById: (id: string) => Promise<Business | null> - Fetch a business by its ID.
 * - createBusiness: (businessData: Omit<Business, ...>) => Promise<void> - Create a new business.
 * - updateBusiness: (businessId: string, businessData: Partial<Business>) => Promise<void> - Update an existing business.
 * - deleteBusiness: (businessId: string) => Promise<void> - Delete a business by ID.
 * - refreshBusinesses: () => Promise<void> - Refresh the list of businesses.
 */
export const BusinessDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id?: string }>();

  const transformBusiness = (row: BusinessRow): Business => ({
    id: row.id,
    businessName: row.business_name,
    ownerName: row.owner_name,
    email: row.email || '',
    phone: row.phone || '',
    address: {
      addressLine1: row.address_line_1 || '',
      addressLine2: row.address_line_2 || '',
      addressCity: row.city || '',
      addressState: row.state || '',
      addressPostalCode: String(row.postal_code || ''),
    },
    businessType: row.business_type as 'independent_contractor',
    status: row.status as 'active' | 'inactive' | 'pending',
    landingPageStatus: row.landing_page_status as 'active' | 'inactive' | 'pending' | undefined,
    slug: row.slug || undefined,
    contractStartDate: row.contract_start_date || '',
    servicesOffered: row.services_offered || [],
    hourlyRate: row.hourly_rate || 0,
    availability: {
      monday: row.monday ?? false,
      tuesday: row.tuesday ?? false,
      wednesday: row.wednesday ?? false,
      thursday: row.thursday ?? false,
      friday: row.friday ?? false,
      saturday: row.saturday ?? false,
      sunday: row.sunday ?? false,
    },
    rating: row.rating || 0,
    completedJobs: row.completed_jobs || 0,
    insuranceInfo: row.insurance_provider ? {
      provider: row.insurance_provider,
      policyNumber: row.insurance_policy_number || '',
      expirationDate: row.insurance_expiration_date || '',
    } : undefined,
    userId: row.user_id || undefined,
    servicePostalCodes: row.service_postal_codes || [],
    clerkOrganizationId: row.clerk_organization_id || undefined,
    subscriptionPlan: row.subscription_plan || undefined,
    hasWebsiteFeatures: row.has_website_feature ?? false,
    freeWebsiteOverride: row.free_website_override ?? undefined,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
  });

  const getBusinessById = useCallback(async (id: string): Promise<Business | null> => {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // PostgREST code for "No rows found"
          return null;
        }
        throw error;
      }
      
      return data ? transformBusiness(data) : null;
    } catch (err) {
      console.error('Error fetching business by ID:', err);
      return null;
    }
  }, []);

  const getBusinessesByPostalCode = useCallback(async (postalCode: string): Promise<Business[]> => {
    try {
      // Use the GIN index on service_postal_codes for efficient Postal code lookup
      // This leverages the idx_businesses_service_postal_codes index from the migration
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .contains('service_postal_codes', [postalCode])
        .eq('status', 'active') // Only include active businesses
        .order('rating', { ascending: false }); // Order by rating for better results

      if (error) throw error;

      return data ? data.map(transformBusiness) : [];
    } catch (err) {
      console.error('Error fetching businesses by Postal code:', err);
      return [];
    }
  }, []);

  const getBusinessesByPostalCodeAndService = useCallback(async (postalCode: string, serviceName: string): Promise<Business[]> => {
    try {
      // First, get the service ID from the service name
      const { data: serviceData, error: serviceError } = await supabase
        .from('services')
        .select('id')
        .eq('name', serviceName)
        .single();

      if (serviceError || !serviceData) {
        console.error('Service not found:', serviceName);
        return [];
      }

      const serviceId = serviceData.id;

      // Get business IDs from business_services junction table for this service
      const { data: businessServiceData, error: businessServiceError } = await supabase
        .from('business_services')
        .select('business_id')
        .eq('service_id', serviceId)
        .eq('is_active', true);

      if (businessServiceError) {
        console.error('Error fetching business_services:', businessServiceError);
        return [];
      }

      if (!businessServiceData || businessServiceData.length === 0) {
        return [];
      }

      const businessIds = businessServiceData.map(bs => bs.business_id);

      // Now fetch businesses that match the postal code AND are in the list from business_services
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .contains('service_postal_codes', [postalCode])
        .in('id', businessIds)
        .eq('status', 'active')
        .order('rating', { ascending: false });

      if (error) throw error;

      return data ? data.map(transformBusiness) : [];
    } catch (err) {
      console.error('Error fetching businesses by Postal code and service:', err);
      return [];
    }
  }, []);

    const refreshBusinesses = useCallback(async () => {
        try {
        setLoading(true);
        setError(null);
        
        let query = supabase.from('businesses').select('*');

        // If an ID is present in the URL, fetch only that business
        if (id) {
            query = query.eq('id', id);
        } else {
            query = query.order('created_at', { ascending: false });
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        const transformedBusinesses = data ? data.map(transformBusiness) : [];
        setBusinesses(transformedBusinesses);
        } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load businesses';
        console.error('Error fetching businesses:', errorMessage);
        setError(errorMessage);
        } finally {
        setLoading(false);
        }
    }, [id]);

  useEffect(() => {
    refreshBusinesses();
  }, [refreshBusinesses]);

  // Setup real-time subscription
  useRealtimeSubscription({
    table: 'businesses',
    filter: 'status=neq.inactive',
    callbacks: {
      onInsert: (payload) => {
        const newBusiness = transformBusiness(payload.new as BusinessRow);
        setBusinesses((prev) => {
          const exists = prev.some((b) => b.id === newBusiness.id);
          if (exists) return prev;
          return [newBusiness, ...prev];
        });
      },
      onUpdate: (payload) => {
        const updatedBusiness = transformBusiness(payload.new as BusinessRow);
        setBusinesses((prev) =>
          prev.map((business) =>
            business.id === updatedBusiness.id ? updatedBusiness : business
          )
        );
      },
      onDelete: (payload) => {
        const deletedId = (payload.old as { id: string }).id;
        setBusinesses((prev) => prev.filter((business) => business.id !== deletedId));
      },
    },
  });

  const createBusiness = useCallback(async (businessData: Omit<Business, 'id' | 'createdAt' | 'updatedAt' | 'rating' | 'completedJobs'>) => {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .insert({
          business_name: businessData.businessName,
          owner_name: businessData.ownerName,
          email: businessData.email,
          phone: businessData.phone,
          // Flatten the address object into the correct database columns
          address_line_1: businessData.address.addressLine1,
          address_line_2: businessData.address.addressLine2,
          city: businessData.address.addressCity,
          state: businessData.address.addressState,
          postal_code: businessData.address.addressPostalCode,
          business_type: businessData.businessType,
          status: businessData.status,
          contract_start_date: businessData.contractStartDate,
          services_offered: businessData.servicesOffered,
          hourly_rate: businessData.hourlyRate,
          monday: businessData.availability.monday,
          tuesday: businessData.availability.tuesday,
          wednesday: businessData.availability.wednesday,
          thursday: businessData.availability.thursday,
          friday: businessData.availability.friday,
          saturday: businessData.availability.saturday,
          sunday: businessData.availability.sunday,
          insurance_provider: businessData.insuranceInfo?.provider,
          insurance_policy_number: businessData.insuranceInfo?.policyNumber,
          insurance_expiration_date: businessData.insuranceInfo?.expirationDate,
          rating: 0, // Default value for new business
          completed_jobs: 0 // Default value for new business
        })
        .select('*') // Fetch from 'businesses' only, not a related table
        .single();

      if (error) throw error;

      // Note: Address needs to be created separately if it's a new one.
      // This implementation assumes address management is handled elsewhere or is not part of the initial creation.

      const newBusiness = transformBusiness(data);
        setBusinesses(prev => [newBusiness, ...prev]);
      } catch (err) {
      console.error('Error adding business:', err);
      throw err;
    }
  }, []);

  /**
   * Maps Business domain model fields to database column names for updates.
   * Reduces cognitive complexity by extracting field mapping logic.
   */
  const mapBusinessToUpdateData = (businessData: Partial<Business>): Record<string, unknown> => {
    const updateData: Record<string, unknown> = {};
    
    // Map simple fields
    const simpleFieldMappings = {
      businessName: 'business_name',
      ownerName: 'owner_name',
      email: 'email',
      phone: 'phone',
      businessType: 'business_type',
      status: 'status',
      contractStartDate: 'contract_start_date',
      servicesOffered: 'services_offered',
      hourlyRate: 'hourly_rate',
      servicePostalCodes: 'service_postal_codes',
      rating: 'rating',
      completedJobs: 'completed_jobs'
    } as const;

    // Apply simple field mappings
    for (const [domainField, dbField] of Object.entries(simpleFieldMappings)) {
      const value = businessData[domainField as keyof Business];
      if (value !== undefined) {
        updateData[dbField] = value;
      }
    }

    // Handle availability mapping
    if (businessData.availability !== undefined) {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
      for (const day of days) {
        updateData[day] = businessData.availability?.[day];
      }
    }

    // Handle address mapping - flatten address object to database columns
    if (businessData.address !== undefined) {
      updateData.address_line_1 = businessData.address.addressLine1;
      updateData.address_line_2 = businessData.address.addressLine2;
      updateData.city = businessData.address.addressCity;
      updateData.state = businessData.address.addressState;
      updateData.postal_code = businessData.address.addressPostalCode;
    }

    // Handle insurance info mapping
    if (businessData.insuranceInfo !== undefined) {
      updateData.insurance_provider = businessData.insuranceInfo?.provider;
      updateData.insurance_policy_number = businessData.insuranceInfo?.policyNumber;
      updateData.insurance_expiration_date = businessData.insuranceInfo?.expirationDate;
    }

    return updateData;
  };

  const updateBusiness = useCallback(async (businessId: string, businessData: Partial<Business>) => {
    try {
      const updateData = mapBusinessToUpdateData(businessData);
      console.log('Updating business with data:', updateData);
      
      const { data, error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId)
        .select('*')
        .single();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update successful, received data:', data);

      const updatedBusiness = transformBusiness(data);
      setBusinesses(prev => prev.map(business => 
        business.id === businessId ? updatedBusiness : business
      ));
    } catch (err) {
      console.error('Error updating business:', err);
      throw err;
    }
  }, []);

  const deleteBusiness = useCallback(async (businessId: string) => {
    try {
      const { error } = await supabase
        .from('businesses')
        .delete()
        .eq('id', businessId);

      if (error) throw error;

      setBusinesses(prev => prev.filter(business => business.id !== businessId));
    } catch (err) {
      console.error('Error deleting business:', err);
      throw err;
    }
  }, []);

  const value: BusinessDataContextType = useMemo(() => ({
    businesses,
    loading,
    error,
    getBusinessById,
    getBusinessesByPostalCode,
    getBusinessesByPostalCodeAndService,
    createBusiness,
    updateBusiness,
    deleteBusiness,
    refreshBusinesses,
  }), [businesses, loading, error, getBusinessById, getBusinessesByPostalCode, getBusinessesByPostalCodeAndService, createBusiness, updateBusiness, deleteBusiness, refreshBusinesses]);

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};
